import settings from 'settings';
import axios from 'axios';
import { fromJS } from 'immutable';
import AuthService from 'library/authService/auth-service';
import { getErrors } from 'selectors/errors';
import { showValidationBlock, validationBlockError } from 'actions/validationBlock';

// A blocked write (FR-5.1) surfaces as HTTP 400 whose body is a list of violation
// objects ({ record_type, record_id, error_type, detail }). Pick those out — and only
// those; a plain DRF error array of strings is left to the normal error handler.
export const extractViolations = (error) => {
  const data = error && error.response && error.response.data;
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter(violation => violation && violation.record_type && violation.error_type);
};

// Endpoints whose responses are large and consumed as plain JS directly. The global
// interceptor otherwise deep-converts every body with fromJS, and the consumer deep-
// converts it straight back with toJS — two full copies of the body. For the roster
// assignment payload (~13.5 MB on a full roster) that round-trip is a measured ~1.5s
// of main-thread work, so we skip it here. Every other endpoint keeps the Immutable
// contract its consumers rely on.
const IMMUTABLE_BYPASS_URLS = ['/api/roster/assignment/get_all/'];

export const shouldBypassImmutable = (url = '') => IMMUTABLE_BYPASS_URLS.some(bypass => url.includes(bypass));

// Apply the Immutable conversion to a response unless its endpoint is bypassed.
// Exported so the behaviour can be unit-tested without registering interceptors.
export const applyResponseData = (response) => {
  const url = (response && response.config && response.config.url) || '';
  if (!shouldBypassImmutable(url)) {
    response.data = fromJS(response.data);
  }
  return response;
};

export const setup = (store) => {
  // ------ Global Axios Defaults ------
  axios.defaults.baseURL = settings.BASE_API_DOMAIN();
  axios.defaults.timeout = 2 * 60 * 1000;
  axios.defaults.xsrfHeaderName = 'X-CSRFToken';
  axios.defaults.xsrfCookieName = 'csrftoken';
  axios.defaults.withCredentials = true;

  // ------ https://github.com/axios/axios#cancellation ------
  // const instance = axios.create();
  // const CancelToken = axios.CancelToken(function (c) { cancel = c });
  // axios.defaults.cancelToken = CancelToken;
  const cancelToken = axios.CancelToken;
  const cancelSource = cancelToken.source();
  // OMG! AXIOS has ISSUES! it merges the cancel token incorrectly, so we trick it and move it from the prototype to the object itself
  cancelSource.token.throwIfRequested = cancelSource.token.throwIfRequested;
  cancelSource.token.promise.then = cancelSource.token.promise.then.bind(cancelSource.token.promise);
  cancelSource.token.promise.catch = cancelSource.token.promise.catch.bind(cancelSource.token.promise);
  axios.defaults.cancelToken = cancelSource.token;

  // ------ Apply token to the request header ------
  axios.interceptors.request.use(
    request => AuthService.getAuthHeader(request.url)
      .then(
        (response) => {
          const authHeader = response;
          if (!authHeader.ignoreToken) {
            request.headers.common.Authorization = authHeader.authorization;
          }
          if (!authHeader.ignoreTenancy) {
            request.headers.common['Site-Tenancy-Id'] = authHeader.tenancyId;
          }
          return request;
        },
        error => Promise.reject(error) // Token Expired. Perform redirect to login page here
      ),
    error => Promise.reject(error)
  );

  // ------ 401 Response Errors - Redirect user to the login ------
  axios.interceptors.response.use(
    response => applyResponseData(response),
    (error) => {
      if (error.response) {
        if (error.response.status >= 500) {
          return Promise.reject(Error('Server error'));
        }
        const violations = extractViolations(error);
        if (error.response.status === 400 && violations.length) {
          // Surface the Optimiser write-block as the single global dialog and reject with
          // a marked, empty error shape so the form's inline alert stays empty AND save
          // handlers can suppress their "save failed" toast — the dialog is the sole error
          // surface (FR-5.1). See isValidationBlockError.
          store.dispatch(showValidationBlock(violations));
          return Promise.reject(validationBlockError());
        }
        return Promise.reject(getErrors(error));
      }
      return Promise.reject(getErrors({
        BHP: error
      }));
    }
  );
};
